import { adminService } from "@/api/admin/admin.service";
import type { TSystemConfig, TSystemConfigField } from "@/api/admin/admin.types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/Checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { Skeleton } from "@/components/ui/Skeleton";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/utils";
import {
  AlertTriangle,
  FileText,
  Flag,
  type LucideIcon,
  RefreshCw,
  Settings2,
  ShieldCheck,
  UserCheck,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

type TCategoryKey = "feature_flags" | "file_limits" | "verification" | "vetting";

const CATEGORY_META: Record<TCategoryKey, { title: string; description: string; icon: LucideIcon }> = {
  feature_flags: {
    title: "Feature Flags",
    description: "Enable or disable platform-wide features.",
    icon: Flag,
  },
  file_limits: {
    title: "File Limits",
    description: "Upload size, allowed types, and retention policy.",
    icon: FileText,
  },
  verification: {
    title: "Verification",
    description: "Operational rate limits and retention for email/phone verification.",
    icon: ShieldCheck,
  },
  vetting: {
    title: "Investor Vetting",
    description: "Operational settings for Sales vetting of investor onboarding.",
    icon: UserCheck,
  },
};

const CATEGORY_ORDER: TCategoryKey[] = ["feature_flags", "file_limits", "verification", "vetting"];

// Local edit value can be bool | string (int/csv typed as string in inputs).
type TDraftValue = boolean | string;

// Safely coerce an unknown JSON value to a display/input string.
function stringifyValue(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return JSON.stringify(value);
}

function fieldToDraft(field: TSystemConfigField): TDraftValue {
  if (field.type === "bool") return Boolean(field.value);
  return stringifyValue(field.value);
}

// Normalise a draft to the payload value the backend expects.
function draftToPayload(field: TSystemConfigField, draft: TDraftValue): boolean | number | string {
  if (field.type === "bool") return Boolean(draft);
  if (field.type === "int") return Number(draft);
  return String(draft);
}

function valuesEqual(field: TSystemConfigField, draft: TDraftValue): boolean {
  if (field.type === "bool") return Boolean(field.value) === Boolean(draft);
  return stringifyValue(field.value) === String(draft);
}

export function SystemConfigPage() {
  const { toast } = useToast();
  const [config, setConfig] = useState<TSystemConfig | null>(null);
  const [drafts, setDrafts] = useState<Record<string, TDraftValue>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const allFields = useMemo<TSystemConfigField[]>(() => {
    if (!config) return [];
    return [...config.feature_flags, ...config.file_limits, ...config.verification, ...(config.vetting ?? [])];
  }, [config]);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    const { data, error: err } = await adminService.getSystemConfig();
    if (err) {
      setError(await extractErrorMessage(err));
    } else if (data?.data) {
      setConfig(data.data);
      const initial: Record<string, TDraftValue> = {};
      for (const cat of CATEGORY_ORDER) {
        for (const f of data.data[cat] ?? []) {
          initial[f.key] = fieldToDraft(f);
        }
      }
      setDrafts(initial);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Keys whose draft differs from the loaded value.
  const changedKeys = useMemo(() => {
    return allFields.filter((f) => f.editable && !valuesEqual(f, drafts[f.key])).map((f) => f.key);
  }, [allFields, drafts]);

  const setDraft = (key: string, value: TDraftValue) => {
    setDrafts((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setConfirmOpen(false);
    if (changedKeys.length === 0) return;
    setIsSaving(true);

    const payload: Record<string, boolean | number | string> = {};
    for (const key of changedKeys) {
      const field = allFields.find((f) => f.key === key)!;
      payload[key] = draftToPayload(field, drafts[key]);
    }

    const { data, error: err } = await adminService.updateSystemConfig(payload);
    if (err) {
      toast({
        title: "Failed to save configuration",
        description: await extractErrorMessage(err),
        variant: "destructive",
      });
    } else if (data?.data) {
      setConfig(data.data);
      const refreshed: Record<string, TDraftValue> = {};
      for (const cat of CATEGORY_ORDER) {
        for (const f of data.data[cat] ?? []) {
          refreshed[f.key] = fieldToDraft(f);
        }
      }
      setDrafts(refreshed);
      toast({
        title: "Configuration saved",
        description: `Updated ${changedKeys.length} setting${changedKeys.length === 1 ? "" : "s"}.`,
      });
    }
    setIsSaving(false);
  };

  const renderField = (field: TSystemConfigField) => {
    const draft = drafts[field.key];
    const isOverride = field.source === "override";

    return (
      <div
        key={field.key}
        className="flex flex-col gap-2 rounded-lg border border-border bg-background p-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4"
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="break-all font-mono text-sm font-medium text-foreground">{field.key}</span>
            {isOverride && (
              <Badge
                variant="secondary"
                className="text-[10px]"
              >
                override
              </Badge>
            )}
            {!field.editable && (
              <Badge
                variant="outline"
                className="text-[10px]"
              >
                read-only
              </Badge>
            )}
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">{field.description}</p>
        </div>

        <div className="flex shrink-0 items-center sm:w-40 sm:justify-end">
          {field.type === "bool" ? (
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={draft === true}
                disabled={!field.editable}
                onCheckedChange={(checked) => setDraft(field.key, checked === true)}
              />
              <span className="text-muted-foreground">{draft === true ? "Enabled" : "Disabled"}</span>
            </label>
          ) : (
            <Input
              className="sm:w-full"
              type={field.type === "int" ? "number" : "text"}
              value={typeof draft === "string" ? draft : ""}
              disabled={!field.editable}
              onChange={(e) => setDraft(field.key, e.target.value)}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="h-full overflow-y-auto bg-background p-4 sm:p-6">
      <div className="mx-auto max-w-4xl space-y-6 pb-24">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="hidden h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary sm:flex">
              <Settings2 className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground sm:text-3xl">System Configuration</h1>
              <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
                Feature flags, file limits, and operational verification settings. Changes persist across restarts and
                apply to all workers.
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void load()}
            disabled={isLoading}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </div>

        {isLoading ? (
          <div className="space-y-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton
                key={i}
                className="h-48 rounded-xl"
              />
            ))}
          </div>
        ) : error ? (
          <div className="flex items-center gap-2 rounded-lg border border-destructive/20 bg-destructive/10 p-4 text-sm text-destructive">
            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            {error}
          </div>
        ) : config ? (
          <>
            {CATEGORY_ORDER.map((cat) => {
              const fields = config[cat] ?? [];
              if (fields.length === 0) return null;
              const { title, description, icon: Icon } = CATEGORY_META[cat];
              const editableCount = fields.filter((f) => f.editable).length;
              return (
                <Card key={cat}>
                  <CardHeader>
                    <div className="flex items-start gap-3">
                      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <CardTitle>{title}</CardTitle>
                          <Badge
                            variant="outline"
                            className="text-[10px]"
                          >
                            {editableCount} editable
                          </Badge>
                        </div>
                        <CardDescription>{description}</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">{fields.map(renderField)}</div>
                  </CardContent>
                </Card>
              );
            })}

            {config.note && <p className="text-xs text-muted-foreground">{config.note}</p>}
          </>
        ) : null}
      </div>

      {/* Sticky save bar — appears when there are unsaved changes */}
      {config && changedKeys.length > 0 && (
        <div className="sticky bottom-0 left-0 right-0 z-10 border-t border-border bg-background/95 px-4 py-3 backdrop-blur duration-200 ease-out animate-in fade-in slide-in-from-bottom-2 supports-[backdrop-filter]:bg-background/80 sm:px-6">
          <div className="mx-auto flex max-w-4xl items-center justify-between gap-3">
            <span className="text-sm text-muted-foreground">
              {changedKeys.length} unsaved change{changedKeys.length === 1 ? "" : "s"}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => void load()}
                disabled={isSaving}
              >
                Discard
              </Button>
              <Button
                disabled={isSaving}
                onClick={() => setConfirmOpen(true)}
              >
                {isSaving ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm dialog */}
      <Dialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save configuration changes?</DialogTitle>
            <DialogDescription>
              These settings apply immediately across all workers and persist across restarts.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-60 space-y-1 overflow-auto rounded-md bg-muted p-3 text-sm">
            {changedKeys.map((key) => {
              const field = allFields.find((f) => f.key === key)!;
              return (
                <div
                  key={key}
                  className="flex items-center justify-between gap-4"
                >
                  <span className="font-mono text-xs">{key}</span>
                  <span className="font-mono text-xs text-muted-foreground">
                    {String(draftToPayload(field, drafts[key]))}
                  </span>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={() => void handleSave()}>Confirm & Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
